import { invariantResponse } from '@epic-web/invariant'
import { Img } from 'openimg/react'
import {
	type LoaderFunctionArgs,
	Form,
	Link,
	useLoaderData,
} from 'react-router'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { prisma } from '#app/utils/db.server.ts'
import { getUserImgSrc } from '#app/utils/misc.tsx'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/index.ts'

export async function loader({ params }: LoaderFunctionArgs) {
	const user = await prisma.user.findFirst({
		select: {
			id: true,
			name: true,
			username: true,
			createdAt: true,
			image: { select: { id: true } },
		},
		where: {
			username: params.username,
		},
	})

	invariantResponse(user, 'User not found', { status: 404 })

	const materials = await prisma.courseMaterial.findMany({
		where: { ownerId: user.id },
		orderBy: { createdAt: 'desc' },
		take: 5,
	})

	const recentAttempts = await prisma.userQuizAttempt.findMany({
		where: { userId: user.id },
		include: { quiz: true },
		orderBy: { createdAt: 'desc' },
		take: 5,
	})

	return {
		user,
		userJoinedDisplay: user.createdAt.toLocaleDateString(),
		materials,
		recentAttempts,
	}
}

export default function ProfileRoute() {
	const data = useLoaderData<typeof loader>()
	const user = data.user
	const userDisplayName = user.name ?? user.username
	const loggedInUser = useOptionalUser()
	const isLoggedInUser = user.id === loggedInUser?.id

	const { materials, recentAttempts } = data

	return (
		<div className="container mt-36 mb-48 flex flex-col items-center justify-center">
			<Spacer size="4xs" />

			<div className="bg-muted container flex flex-col items-center rounded-3xl p-12">
				<div className="relative w-52">
					<div className="absolute -top-40">
						<div className="relative">
							<Img
								src={getUserImgSrc(data.user.image?.id)}
								alt={userDisplayName}
								className="size-52 rounded-full object-cover"
								width={832}
								height={832}
							/>
						</div>
					</div>
				</div>

				<Spacer size="sm" />

				<div className="flex flex-col items-center">
					<div className="flex flex-wrap items-center justify-center gap-4">
						<h1 className="text-h2 text-center">{userDisplayName}</h1>
					</div>
					<p className="text-muted-foreground mt-2 text-center">
						Joined {data.userJoinedDisplay}
					</p>
					{isLoggedInUser ? (
						<Form action="/logout" method="POST" className="mt-3">
							<Button type="submit" variant="link" size="pill">
								<Icon name="exit" className="scale-125 max-md:scale-150">
									Logout
								</Icon>
							</Button>
						</Form>
					) : null}
					<div className="mt-10 flex gap-4">
						{isLoggedInUser ? (
							<>
								<Button asChild>
									<Link to="materials/new" prefetch="intent">
										+ New Course Material
									</Link>
								</Button>
								<Button asChild>
									<Link to="/settings/profile" prefetch="intent">
										Edit profile
									</Link>
								</Button>
								<Button asChild>
									<Link to="quizzes" prefetch="intent">
										My Quizzes
									</Link>
								</Button>
							</>
						) : null}
					</div>

					{isLoggedInUser ? (
						<div className="mt-12 grid w-full gap-10 md:grid-cols-2">
							<div className="space-y-4">
								<h2 className="text-h3">My Course Materials</h2>
								{materials.length === 0 ? (
									<p className="text-muted-foreground">
										No materials uploaded yet.
									</p>
								) : (
									<ul className="space-y-3">
										{materials.map((material) => (
											<li
												key={material.id}
												className="hover:bg-muted/50 rounded-lg border p-4 transition"
											>
												<Link to={`materials/${material.id}`} className="block">
													<h3 className="font-semibold">{material.title}</h3>
													<p className="text-muted-foreground truncate text-sm">
														{material.description || 'No description'}
													</p>
													<div className="mt-2 flex gap-2">
														<span
															className={`rounded-full px-2 py-0.5 text-xs ${material.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
														>
															{material.status}
														</span>
														<span className="text-muted-foreground text-xs">
															{new Date(
																material.createdAt,
															).toLocaleDateString()}
														</span>
													</div>
												</Link>
											</li>
										))}
									</ul>
								)}
							</div>
							<div className="space-y-4">
								<h2 className="text-h3">Recent Activity</h2>
								{recentAttempts.length === 0 ? (
									<p className="text-muted-foreground">No quizzes taken yet.</p>
								) : (
									<ul className="space-y-3">
										{recentAttempts.map((attempt) => (
											<li
												key={attempt.id}
												className="hover:bg-muted/50 rounded-lg border p-4 transition"
											>
												<Link
													to={`quizzes/result?attemptId=${attempt.id}`}
													className="block"
												>
													<h3 className="font-semibold">
														{attempt.quiz.title}
													</h3>
													<div className="mt-1 flex items-center justify-between">
														<span className="font-mono font-medium">
															Score: {attempt.score}/{attempt.maxScore}
														</span>
														<span className="text-muted-foreground text-xs">
															{new Date(attempt.createdAt).toLocaleDateString()}
														</span>
													</div>
												</Link>
											</li>
										))}
									</ul>
								)}
							</div>
						</div>
					) : null}
				</div>
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = ({ data, params }) => {
	const displayName = data?.user.name ?? params.username
	return [
		{ title: `${displayName} | Epic Quizzes` },
		{
			name: 'description',
			content: `Profile of ${displayName} on Epic Quizzes`,
		},
	]
}

export function ErrorBoundary() {
	return (
		<GeneralErrorBoundary
			statusHandlers={{
				404: ({ params }) => (
					<p>No user with the username "{params.username}" exists</p>
				),
			}}
		/>
	)
}
